import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AlertTriangle } from 'lucide-react'

import {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from '@/components/ResponsiveModal/ResponsiveModal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useDeleteAccountMutation } from '@/store/api/Users/Users.query'
import { useLogout } from '@/hooks/useLogout'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'

export const DeleteAccountPopup = () => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [deleteAccount, { isLoading }] = useDeleteAccountMutation()
  const { logout } = useLogout()

  const confirmWord = t('dashboard.settings.deleteAccountPopup.confirmWord')
  const isConfirmValid = confirmText === confirmWord

  const handleDelete = async () => {
    if (!isConfirmValid) return

    try {
      await deleteAccount().unwrap()
      toast.success(t('dashboard.settings.deleteAccountPopup.successMessage'))
      await logout()
    } catch (error) {
      toast.error(t('dashboard.settings.deleteAccountPopup.errorMessage'))
      catchWithSentry(error)
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      setConfirmText('')
    }
  }

  return (
    <ResponsiveModal open={open} onOpenChange={handleOpenChange}>
      <ResponsiveModalTrigger asChild>
        <Button className="font-medium text-sm h-[42px] px-5 bg-dynamicRed hover:bg-dynamicRed/90 text-staticWhite max-md:w-full">
          {t('dashboard.settings.deleteAccountButton')}
        </Button>
      </ResponsiveModalTrigger>
      <ResponsiveModalContent
        className="bg-staticWhite flex flex-col gap-6 md:max-w-md p-6"
        side="bottom"
      >
        <ResponsiveModalHeader className="flex flex-col items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-alertRedBase flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-dynamicRed" />
          </div>
          <ResponsiveModalTitle className="text-2xl font-semibold text-neutralColor">
            {t('dashboard.settings.deleteAccountPopup.title')}
          </ResponsiveModalTitle>
        </ResponsiveModalHeader>

        <p className="text-neutralColor80 text-sm">
          {t('dashboard.settings.deleteAccountPopup.description')}
        </p>

        <div className="flex flex-col gap-2">
          <p className="text-neutralColor text-sm font-medium">
            {t('dashboard.settings.deleteAccountPopup.typeToConfirm')}{' '}
            <span className="font-bold">{confirmWord}</span>{' '}
            {t('dashboard.settings.deleteAccountPopup.toConfirm')}
          </p>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={t(
              'dashboard.settings.deleteAccountPopup.inputPlaceholder',
            )}
            className="rounded-xl border-neutralColor30 bg-staticWhite text-neutralColor"
          />
        </div>

        <div className="flex gap-3 justify-end">
          <ResponsiveModalClose asChild>
            <Button
              type="button"
              variant="outline"
              className="font-medium text-sm h-[42px] px-5 rounded-xl border-neutralColor30 text-neutralColor"
            >
              {t('common.cancel')}
            </Button>
          </ResponsiveModalClose>
          <Button
            onClick={handleDelete}
            disabled={!isConfirmValid || isLoading}
            isLoading={isLoading}
            type="button"
            className="font-medium text-sm h-[42px] px-5 rounded-xl bg-dynamicRed hover:bg-dynamicRed/90 text-staticWhite"
          >
            {t('dashboard.settings.deleteAccountPopup.deleteButton')}
          </Button>
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
